import { addDays, addMonths, endOfDay } from "date-fns";
import isNil from "lodash-es/isNil";
import { makeAutoObservable } from "mobx";
import type { ClaimFormValues } from "../../account/claims/create-claim/ClaimForm";
import { apiClient } from "../../common/apiClient";
import { logger } from "../../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import { sendNewClaim } from "../../common/tracking";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { Claim, ClaimCause, NewClaimProduct } from "../../common/types/claimTypes";
import type { Invoice, InvoiceByOrderResponse } from "../../common/types/invoiceTypes";
import type { M3OrderListed, OrderFilter } from "../../common/types/m3Types";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../../common/utils/asyncDataUtils";
import { ZClaim } from "../../common/zodSchemas/claim";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import orderStore from "../orders/orderStore";
import { fetchOrderListFromM3 } from "../orders/orderUtilsAndApi";

type DatesTuple = [Date, Date];

const defaultStart = addMonths(new Date(), -12);
const defaultEnd = endOfDay(new Date());

type ClaimsFilters = {
   isNotProcessed: boolean | null;
   orderNumber: string;
   dateRange: {
      from: Date;
      to: Date;
   };
};

type ClaimsStoreType = BaseStoreType & {
   eligibleOrders: AsyncData<M3OrderListed[]>;
   claims: AsyncData<Claim[]>;
   currentClaimInvoices: AsyncData<Invoice[]>;
   claimSubmission: AsyncData<null>;
   filters: ClaimsFilters;

   filterAsPickerDates(): DatesTuple;
   fetchEligibleOrders(): Promise<void>;
   fetchClaims(): Promise<void>;
   fetchCurrentClaimInvoices(orderNumber: string): void;
   submitClaim(orderNumber: string, values: ClaimFormValues): Promise<string | undefined>;
   resetClaimSubmission(): void;
   getCauses(): ClaimCause[];
};

const getPayloadFromState = async (
   claimData: {
      source: string;
      orderNumber: string;
      customerNumber: string;
      contactName: string;
      contactEmail: string;
      contactPhone: string;
      reasonDescription: string;
      images: File[];
      orderLines: NewClaimProduct[];
   },
   orderNumber: string
): Promise<Record<string, unknown>> => {
   const payload = {
      ...claimData,
      orderNumber,
      customerNumber: authStore.currentCompany ?? ""
   };

   if (claimData.images.length > 0 && claimData.images[0] instanceof File) {
      try {
         const imageData = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error("Failed to read image file"));
            reader.readAsDataURL(claimData.images[0] as File);
         });

         const { images: _images, ...payloadWithoutImages } = payload;
         return { ...payloadWithoutImages, imageData: { fileName: claimData.images[0].name, base64Image: imageData } };
      } catch (error) {
         logger.error("claims", "Error processing image for claim:", error);
         const { images: _images, ...payloadWithoutImages } = payload;
         return { ...payloadWithoutImages };
      }
   }

   const { images: _images, ...payloadWithoutImages } = payload;
   return { ...payloadWithoutImages };
};

const buildOrderLinesFromFormValues = (values: ClaimFormValues): NewClaimProduct[] => {
   const selectedSkus = Object.entries(values)
      .filter(([key, value]) => key.startsWith("selected_") && value === true)
      .map(([key]) => key.replace("selected_", ""));

   return selectedSkus.map((sku) => ({
      sku,
      quantity: Number(values[`quantity_${sku}`]),
      reasonCode: values[`cause_${sku}`] as string,
      bestBeforeDate: values[`bestBefore_${sku}`] as string,
      productionBatchNumber: values[`batchNumber_${sku}`] as string
   }));
};

const claimsStore: ClaimsStoreType = makeAutoObservable({
   eligibleOrders: initializeWithDefaultData([]),
   claims: initializeWithDefaultData([]),
   currentClaimInvoices: initializeWithDefaultData([]),
   claimSubmission: initializeWithDefaultData(null),
   filters: {
      isNotProcessed: null,
      orderNumber: "",
      dateRange: {
         from: defaultStart,
         to: defaultEnd
      }
   },

   filterAsPickerDates: (): DatesTuple => {
      const res = [claimsStore.filters.dateRange.from, claimsStore.filters.dateRange.to];
      return res.filter((date): date is Date => date !== null) as DatesTuple;
   },

   resetClaimSubmission: () => {
      claimsStore.claimSubmission = initializeWithDefaultData(null);
   },

   fetchEligibleOrders: async () => {
      const filtersNeeded: OrderFilter = {
         orderNumber: "",
         orderName: "",
         status: "",
         dateRange: {
            from: addDays(new Date(), -4),
            to: addDays(new Date(), 30)
         }
      };
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         return Promise.reject("Need to be logged in to fetch eligible orders for claims");
      }

      setAsWaitingForData(claimsStore.eligibleOrders);

      try {
         const possibleOrders = await fetchOrderListFromM3(theme.m3CompanyNumber, customerNumber, filtersNeeded);
         const eligible: M3OrderListed[] = [];

         for (const candicateOrderDetails of possibleOrders) {
            if (
               "canSendComplaint" in candicateOrderDetails &&
               candicateOrderDetails.canSendComplaint &&
               !candicateOrderDetails.isTemporaryOrder
            ) {
               void orderStore.fetchOrderDetails(candicateOrderDetails.orderNumber);
               eligible.push(candicateOrderDetails);
            }
         }

         setAsDataAvailable(claimsStore.eligibleOrders, eligible);
      } catch (err) {
         setAsErrorOccured(claimsStore.eligibleOrders, `${err}`);
      }
   },

   fetchClaims: async () => {
      if (!authStore.isLoggedIn() || isNil(authStore.currentCompany)) {
         return Promise.reject();
      }

      setAsWaitingForData(claimsStore.claims);
      const url = resolvePlatformRequestUrl(platformRoutes.claims, { customerNumber: authStore.currentCompany });
      return apiClient(url, authStore.getSessionToken())
         .get()
         .res()
         .then(async (response) => {
            if (response.status === 206) {
               logger.warn("claims", "Partial content (206) when fetching claims");
            }

            const res = await response.json();

            if (!Array.isArray(res)) {
               throw new Error(`Expected array of claims, got: ${typeof res}`);
            }

            const claims: Claim[] = [];
            for (const claimObj of res as Claim[]) {
               const parseResult = ZClaim.safeParse(claimObj);
               if (parseResult.success) {
                  claims.push({
                     ...parseResult.data,
                     products: parseResult.data.products ?? []
                  });
               } else {
                  logger.error("claims", "Invalid claim object:", claimObj, parseResult.error);
               }
            }
            setAsDataAvailable(claimsStore.claims, claims);
         })
         .catch((errorObj) => {
            logger.error("claims", "Error fetching claims:", errorObj);
            setAsErrorOccured(claimsStore.claims, `${errorObj}`);
         });
   },

   fetchCurrentClaimInvoices: async (orderNumber: string) => {
      const customerNumber = authStore.currentCompany;
      if (!authStore.isLoggedIn() || isNil(customerNumber) || orderNumber === "") {
         return Promise.reject([]);
      }

      setAsWaitingForData(claimsStore.currentClaimInvoices);
      const url = resolvePlatformRequestUrl(platformRoutes.invoicesForOrder, {
         customerNumber,
         orderNumber
      });
      return apiClient(url, authStore.getSessionToken())
         .get()
         .json()
         .then((res) => {
            const response = res as InvoiceByOrderResponse;
            if (!res || response.creditLookupStatus === "FAILED" || response.creditLookupMessage) {
               logger.error(
                  "claims",
                  "Error fetching invoices for ordernumber:",
                  orderNumber,
                  response.creditLookupMessage || "Failed to fetch invoices"
               );
               setAsErrorOccured(claimsStore.currentClaimInvoices, response.creditLookupMessage || "Failed to fetch invoices");
               return;
            }
            setAsDataAvailable(claimsStore.currentClaimInvoices, response.invoices);
         })
         .catch((errorObj) => {
            setAsErrorOccured(claimsStore.currentClaimInvoices, `${errorObj}`);
         });
   },

   submitClaim: async (orderNumber, values) => {
      setAsWaitingForData(claimsStore.claimSubmission);

      const source = theme.storeId === "tinehandel" ? "TINE Handel" : "";
      const orderLines = buildOrderLinesFromFormValues(values);

      const claimData = {
         source,
         orderNumber,
         customerNumber: authStore.currentCompany ?? "",
         contactName: values.contactName as string,
         contactEmail: values.contactEmail as string,
         contactPhone: values.contactPhone as string,
         reasonDescription: values.reasonDescription as string,
         images: values.image ? [values.image as File] : [],
         orderLines
      };

      const formPayload = await getPayloadFromState(claimData, orderNumber);

      logger.log("claims", "Sending claim", {
         orderNumber,
         orderLinesCount: orderLines.length,
         hasImage: Boolean(values.image)
      });
      try {
         const url = resolvePlatformRequestUrl(platformRoutes.claimSubmit, {});
         const response = await apiClient(url, authStore.getSessionToken())
            .headers({ "Content-Type": "application/json" })
            .body(JSON.stringify(formPayload))
            .post()
            .res();
         const data = (await response.json()) as { messageId: string };
         sendNewClaim({
            orderNumber,
            lines:
               orderLines?.map((p) => ({
                  sku: p.sku,
                  cause_code: p.reasonCode,
                  cause_description: claimsStore.getCauses().find((c) => c.code === p.reasonCode)?.label || ""
               })) || []
         });
         claimsStore.resetClaimSubmission();
         return data.messageId;
      } catch (error) {
         logger.error("claims", "Error submitting claim:", error);
         const message = "Vi greier ikke å sende inn reklamasjonen din.";
         setAsErrorOccured(claimsStore.claimSubmission, message);
         return undefined;
      }
   },

   getCauses: () => [
      { code: "R01", label: "Kort holdbarhetsdato" },
      { code: "R02", label: "Kvalitetsfeil" },
      { code: "R04", label: "Transportskade" },
      { code: "R06", label: "For få produkter levert" },
      { code: "R07", label: "Feil produkt levert" },
      { code: "U08", label: "Feil produkt bestilt" }
   ],

   clearCompanySpecificData: () => {
      claimsStore.claims = initializeWithDefaultData([]);
      claimsStore.eligibleOrders = initializeWithDefaultData([]);
      claimsStore.claimSubmission = initializeWithDefaultData(null);
      claimsStore.currentClaimInvoices = initializeWithDefaultData([]);
   }
} satisfies ClaimsStoreType);

export default claimsStore;
